import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { JSONContent } from '@tiptap/core';

import { useCollaborativeDocumentContext } from '../../contexts';
import { markdownToContentJson } from '../../utils';
import { useTiptapCollaboration } from '../useTiptapCollaboration';
import { useUpdateCollaborativeDocument } from '../useUpdateCollaborativeDocument';

const PERSIST_DEBOUNCE_MS = 500;

const cloneJsonContent = (content: JSONContent): JSONContent =>
  structuredClone(content);

type PendingContent = {
  content: JSONContent;
  serialized: string;
};

const hasStoredDocumentContent = (
  content: unknown,
  markdown?: string | null,
): boolean => {
  if (typeof markdown === 'string' && markdown.trim()) {
    return true;
  }

  if (typeof content === 'string' && content.trim()) {
    return true;
  }

  return Boolean(content && typeof content === 'object');
};

const isEmptyDocumentContent = (content: JSONContent): boolean => {
  if (content.type !== 'doc') {
    return false;
  }

  const nodes = content.content ?? [];
  if (nodes.length === 0) {
    return true;
  }

  return nodes.every(node => {
    if (node.type !== 'paragraph' && node.type !== 'heading') {
      return false;
    }
    return !node.content || node.content.length === 0;
  });
};

const getStoredInitialContent = (
  content: unknown,
  markdown?: string | null,
): JSONContent | undefined => {
  if (content && typeof content === 'object') {
    return cloneJsonContent(content as JSONContent);
  }

  if (typeof markdown === 'string' && markdown.trim()) {
    return markdownToContentJson(markdown);
  }

  if (typeof content === 'string' && content.trim()) {
    return markdownToContentJson(content);
  }

  return undefined;
};

export const useTiptapPersistence = () => {
  const { collaborativeDocument } = useCollaborativeDocumentContext();
  const isRealtimeEditing = collaborativeDocument.realtimeEditing === true;
  const lastPersistedContentRef = useRef<string | null>(null);
  const loadedWithStoredContentRef = useRef(false);
  const skippedInitialEmptyContentRef = useRef(false);
  const pendingContentRef = useRef<PendingContent | null>(null);
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { collaboration, providerError } = useTiptapCollaboration({
    documentName: collaborativeDocument.name,
    enabled: isRealtimeEditing,
  });

  const { updateCollaborativeDocument } = useUpdateCollaborativeDocument({
    onError: 'post-alert',
  });

  const initialContent = useMemo(
    () =>
      !isRealtimeEditing
        ? getStoredInitialContent(
            collaborativeDocument.content,
            collaborativeDocument.markdown,
          )
        : undefined,
    [
      collaborativeDocument.content,
      collaborativeDocument.markdown,
      isRealtimeEditing,
    ],
  );

  useEffect(() => {
    loadedWithStoredContentRef.current = hasStoredDocumentContent(
      collaborativeDocument.content,
      collaborativeDocument.markdown,
    );
    skippedInitialEmptyContentRef.current = false;
    lastPersistedContentRef.current = initialContent
      ? JSON.stringify(initialContent)
      : null;
  }, [
    collaborativeDocument.content,
    collaborativeDocument.markdown,
    initialContent,
  ]);

  const flushPendingContent = useCallback(async () => {
    const pending = pendingContentRef.current;
    if (!pending) {
      return;
    }

    pendingContentRef.current = null;
    if (pending.serialized === lastPersistedContentRef.current) {
      return;
    }

    await updateCollaborativeDocument(
      {
        name: collaborativeDocument.name,
        content: pending.content,
      },
      'content',
    );
    lastPersistedContentRef.current = pending.serialized;
  }, [collaborativeDocument.name, updateCollaborativeDocument]);

  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
        persistTimeoutRef.current = null;
        void flushPendingContent();
      }
    };
  }, [flushPendingContent]);

  const persistContent = useCallback(
    (content: JSONContent) => {
      if (isRealtimeEditing) {
        return;
      }

      const serialized = JSON.stringify(content);
      if (serialized === lastPersistedContentRef.current) {
        return;
      }

      // TipTap can briefly emit an empty document while hydrating stored JSON.
      // Ignore that first empty update, then persist later edits using the
      // serialized clone as both a stable equality key and mutation boundary.
      if (
        loadedWithStoredContentRef.current &&
        !skippedInitialEmptyContentRef.current &&
        isEmptyDocumentContent(content)
      ) {
        skippedInitialEmptyContentRef.current = true;
        return;
      }

      pendingContentRef.current = {
        content: structuredClone(content),
        serialized,
      };
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }
      persistTimeoutRef.current = setTimeout(() => {
        persistTimeoutRef.current = null;
        flushPendingContent();
      }, PERSIST_DEBOUNCE_MS);
    },
    [flushPendingContent, isRealtimeEditing],
  );

  const markContentAsPersisted = useCallback((content: JSONContent) => {
    lastPersistedContentRef.current = JSON.stringify(content);
  }, []);

  const contentVersion = isRealtimeEditing
    ? undefined
    : collaborativeDocument.updateTime;

  return {
    collaborationExtension: collaboration.collaborationExtension,
    contentVersion,
    initialContent,
    isRealtimeEditing,
    markContentAsPersisted,
    persistContent,
    providerError,
  };
};
